/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */

import { Position } from "@xyflow/react";
import { NodeDescriptor, ChunkType } from "../Api/Api";
import { Path, PathEntry, PathType } from "../Api/DataTransformation";
import { deepEquals } from "../Utils/Comparison";
import { NumberBucket } from "../Utils/Types";
import {
  ReactFlowNode,
  TransformEngineNodeData,
  TransformEngineNodeEntry,
  TransformEngineNodeEntryId,
  TransformEngineNodeHandle,
  TransformEngineNodeHandleId,
} from "./TransformNodes/Util/TransformEngineDataUtils";
import { NODE_TYPES } from "../Shared/TransformNodeTypes";

export const TRANSFORM_NODE_ID_TO_TYPE = {
  "88c0b853-dfcd-4768-93b3-d37f3a070637": NODE_TYPES.protoToXml_full,
  "a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d": NODE_TYPES.protoToXml_prim,
  "6a908eb2-5da6-43fe-8e69-8c76960305f1": NODE_TYPES.xmlToProto_full,
  "f3b1a2c9-7e4d-4b8f-9c21-6a5e8d4f2b90": NODE_TYPES.xmlToProto_prim,
  "c1f27fd0-5a3a-43ab-b67f-6c22df024000": NODE_TYPES.output,
  "88b090ea-5708-45f2-8f85-c38c06d0e48b": NODE_TYPES.composer,
} as const;

export const TRANSFORM_NODE_TYPE_TO_ID = Object.fromEntries(
  Object.entries(TRANSFORM_NODE_ID_TO_TYPE).map(([id, type]) => [type, id])
) as {
  [K in (typeof TRANSFORM_NODE_ID_TO_TYPE)[keyof typeof TRANSFORM_NODE_ID_TO_TYPE]]: string;
};

/**
 * Creates a ReactFlowNode with the specified ID and position, and suitable for the specified descriptor.
 */
export function nodeFromDescriptor(
  dataID: string,
  descriptor: NodeDescriptor,
  position: ReactFlowNode["position"]
): ReactFlowNode {
  // get all entries for all paths (start index at 1)
  const entries = descriptor.allPaths.map((path, index) =>
    createEntryForPath(descriptor, index + 1, path)
  );

  // set up a counter to keep track of all IDs assigned to handles (start at 1)
  const handleIdCounter: NumberBucket = { value: 1 };

  // create handles for entries
  // (each entry has as many handles as path junctions)
  const entryHandles = entries.flatMap((entry) =>
    createHandlesForEntry(handleIdCounter, entry)
  );

  const parentHandlePath: Path = {
    nodeDescriptorID: descriptor.id,
    pathType: PathType.INPUT,
    pathEntries: entries,
  };

  const parentHandle: TransformEngineNodeHandle = {
    id: 0 as TransformEngineNodeHandleId,
    path: parentHandlePath,
    handlePosition: Position.Right,
  };

  const handles = [parentHandle, ...entryHandles];

  const nodeData: TransformEngineNodeData = {
    dataID,
    title: descriptor.displayName,
    descriptor,
    entries,
    handles,
    mappingResult: null,
  };

  // the following UUIDs will always correspond to their respective formats
  let type = undefined;
  switch (descriptor.id) {
    case "88c0b853-dfcd-4768-93b3-d37f3a070637":
      type = NODE_TYPES.protoToXml_full;
      break;
    case "a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d":
      type = NODE_TYPES.protoToXml_prim;
      break;
    case "6a908eb2-5da6-43fe-8e69-8c76960305f1":
      type = NODE_TYPES.xmlToProto_full;
      break;
    case "f3b1a2c9-7e4d-4b8f-9c21-6a5e8d4f2b90":
      type = NODE_TYPES.xmlToProto_prim;
      break;
    case "c1f27fd0-5a3a-43ab-b67f-6c22df024000":
      type = NODE_TYPES.output;
      break;
    case "88b090ea-5708-45f2-8f85-c38c06d0e48b":
      type = NODE_TYPES.composer;
      break;
    default:
      type = NODE_TYPES.input;
  }

  return {
    id: dataID,
    type,
    position,
    data: { ...nodeData },
    measured: { width: 0, height: 0 },
  };
}

/**
 * Returns the index of the last path element.
 */
export function lastPathElementIndex(path: Path | PathEntry[]): number {
  const pathEntries =
    (path as Path).pathEntries !== undefined
      ? (path as Path).pathEntries
      : (path as PathEntry[]);

  return pathEntries.length - 1;
}

/**
 * Returns the last path element.
 */
export function lastPathElement(path: Path | PathEntry[]): PathEntry {
  const pathEntries =
    (path as Path).pathEntries !== undefined
      ? (path as Path).pathEntries
      : (path as PathEntry[]);

  if (pathEntries.length == 0) {
    return undefined;
  }
  return pathEntries[pathEntries.length - 1];
}

/**
 * Creates a node entry corresponding to this path.
 */
export function createEntryForPath(
  descriptor: NodeDescriptor,
  entryIndex: number,
  path: Path
): TransformEngineNodeEntry {
  const lastElementIndex = lastPathElementIndex(path);
  if (lastElementIndex < 0) {
    return undefined;
  }

  // we currently write a single entry regardless of whether it's a range
  return entryFromLastPathElement(
    descriptor,
    entryIndex as TransformEngineNodeEntryId,
    path.pathType,
    path.pathEntries
  );
}

/**
 * Creates all handles needed for this entry: one handle per path junction.
 */
export function createHandlesForEntry(
  handleIdCounter: NumberBucket,
  entry: TransformEngineNodeEntry
): TransformEngineNodeHandle[] {
  const handles = new Array<TransformEngineNodeHandle>();

  // generate handles for every path subset except the end
  // eslint-disable-next-line no-loops/no-loops
  for (
    let subsetLength = 1;
    subsetLength < entry.path.pathEntries.length;
    subsetLength++
  ) {
    const subsetElements = pathSubset(entry.path.pathEntries, subsetLength);
    const subsetPath: Path = { ...entry.path, pathEntries: subsetElements };

    const handle: TransformEngineNodeHandle = {
      id: handleIdCounter.value++ as TransformEngineNodeHandleId,
      path: subsetPath,
    };
    handles.push(handle);
  }

  // generate a handle for the end
  const lastHandle: TransformEngineNodeHandle = {
    id: handleIdCounter.value++ as TransformEngineNodeHandleId,
    entryId: entry.id,
    path: entry.path,
  };
  handles.push(lastHandle);

  return handles;
}

/**
 * Creates a default label suitable for this path element.
 */
export function labelFromPathElement(
  _pathType: PathType,
  element: PathEntry
): string {
  return element.byString;
}

/**
 * Returns a label suitable for display, taking into account possible omitted values.
 */
export function displayableLabel(
  defaultLabel: string,
  label?: string,
  _labelRange?: string
): string {
  return (label ?? "").length > 0 ? label : defaultLabel;
}

/**
 * Creates a node entry from the last element in this path.
 */
export function entryFromLastPathElement(
  descriptor: NodeDescriptor,
  id: TransformEngineNodeEntryId,
  pathType: PathType,
  pathEntries: PathEntry[]
): TransformEngineNodeEntry {
  const lastElementIndex = lastPathElementIndex(pathEntries);
  if (lastElementIndex < 0) {
    return undefined;
  }
  const lastElement = pathEntries[lastElementIndex];
  const label = labelFromPathElement(pathType, lastElement);

  return {
    id,
    originalLabel: true,
    label,
    path: { nodeDescriptorID: descriptor.id, pathType, pathEntries },
    isChild: lastElement.isChild ? true : false,
  };
}

/**
 * Figures out what chunk type corresponds to this position in the format or transform.
 */
export function determineChunkType(
  descriptor: NodeDescriptor,
  pathEntries: PathEntry[]
): ChunkType {
  if (pathEntries.length == 0) {
    return "NONE";
  }

  // find other paths that are longer than this path
  const successors = successorPaths(descriptor.allPaths, pathEntries);

  // if none, it's an item
  if (successors.length == 0) {
    return "ITEM";
  }

  return "MAP_GROUP";
}

/**
 * Shifts the last path element index by the specified delta.  Non-numeric paths are not affected.
 */
export function modifyPathEndpoint(path: Path, delta: number): Path {
  // divide up the path
  const lastElementIndex = lastPathElementIndex(path);
  if (lastElementIndex < 0) {
    return path;
  }
  const firstElements = path.pathEntries.slice(0, lastElementIndex);
  const lastElement = path.pathEntries[lastElementIndex];

  // make a new set of elements
  const newElements = firstElements.concat(
    modifyPathElement(lastElement, delta)
  );

  return { ...path, pathEntries: newElements };
}

/**
 * Shifts this path element index by the specified delta.  Non-numeric path elements are not affected.
 */
function modifyPathElement(pathEntry: PathEntry, _delta: number): PathEntry {
  return pathEntry;
}

/**
 * Returns whether the sequence of path elements begins with the prefix subsequence.
 */
export function pathStartsWith(
  pathEntries: PathEntry[],
  prefix: PathEntry[]
): boolean {
  if (pathEntries.length < prefix.length) {
    return false;
  }
  const candidates = pathEntries.slice(0, prefix.length);
  return arePathsEqual(candidates, prefix);
}

/**
 * Returns all paths that could be immediate successors of the provided path, based on the provided array.
 */
export function successorPaths(
  allPaths: Path[],
  pathEntries: PathEntry[]
): PathEntry[][] {
  if (pathEntries.length == 0) {
    return allPaths
      .filter((path) => path.pathEntries.length == 1)
      .map((path) => path.pathEntries);
  }

  const somePaths = new Array<PathEntry[]>();

  // find all paths that could be successor paths
  allPaths.forEach((candidatePath) => {
    if (
      pathStartsWith(candidatePath.pathEntries, pathEntries) &&
      candidatePath.pathEntries.length > pathEntries.length
    ) {
      candidatePath.pathEntries;
      somePaths.push(candidatePath.pathEntries);
    }
  });

  // make the collection unique
  return somePaths.filter(
    (list_i, i) =>
      somePaths.find((list_j, j) => i != j && deepEquals(list_i, list_j)) ==
      undefined
  );
}

/**
 * Returns a subset of the path elements.
 */
export function pathSubset(
  pathEntries: PathEntry[],
  length: number
): PathEntry[] {
  return pathEntries.slice(0, length);
}

/**
 * Returns the path immediately preceding this one.
 */
export function pathParent(pathEntries: PathEntry[]): PathEntry[] {
  if (pathEntries.length > 0) {
    const lastIndex = lastPathElementIndex(pathEntries);
    if (lastIndex > 0) {
      return pathEntries.slice(0, lastIndex);
    }
  }
  return [];
}

/**
 * Returns whether two paths are equal.
 */
export function arePathsEqual(
  pathElements1: PathEntry[],
  pathElements2: PathEntry[]
): boolean {
  if (pathElements1.length != pathElements2.length) {
    return false;
  }
  return deepEquals(pathElements1, pathElements2);
}
